import React, { useCallback } from 'react'
import { useFormikContext } from 'formik'
import ConfigurationPanelFields from 'src/components/configuration-panel/ConfigurationPanelFields'
import { buildGroupedTenantConfigurations } from 'src/utils/tenant-configuration'
import { Box, Button, Row, Select, Stack, Text } from 'src/ui'
import { type PlatformLocationFormValues } from './platform-location-form'
import { useGetCommoditiesForOverride } from 'src/api/queries/microservices/platform-markets-location-config'

type Props = {
  canEdit: boolean
  commodityConfigurables: TenantConfigResponse[]
  datasourceProviderId: string
}

export default function PlatformLocationConfigPageCommodityOverridesSection({
  canEdit,
  commodityConfigurables,
  datasourceProviderId,
}: Props) {
  const { values, setFieldValue } = useFormikContext<PlatformLocationFormValues>()

  const { data: commodities, isSuccess: areCommoditiesReady } =
    useGetCommoditiesForOverride(datasourceProviderId)

  const addCommodityOverride = useCallback(
    (commodityId?: string) => {
      const commodity = commodities?.find((option) => option.commodityId === commodityId)
      if (!commodity) return

      setFieldValue('commodityOverrides', [
        ...values.commodityOverrides,
        {
          commodityId: commodity.commodityId,
          commodityName: commodity.commodityName,
          fields: buildGroupedTenantConfigurations(commodityConfigurables),
        },
      ])
    },
    [commodities, commodityConfigurables, setFieldValue, values.commodityOverrides]
  )

  const removeCommodityOverride = useCallback(
    (commodityId: string) => {
      setFieldValue(
        'commodityOverrides',
        values.commodityOverrides.filter((override) => override.commodityId !== commodityId)
      )
    },
    [setFieldValue, values.commodityOverrides]
  )

  const selectedCommodityIds = new Set(
    values.commodityOverrides.map((override) => override.commodityId)
  )
  const commodityOptions =
    commodities
      ?.filter((commodity) => !selectedCommodityIds.has(commodity.commodityId))
      ?.map((commodity) => ({ value: commodity.commodityId, label: commodity.commodityName })) ?? []

  return (
    <Box borderTopWidth="1px" borderColor="gray.200" px={6} py={4}>
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        Commodity Overrides
      </Text>

      <Box mb={4}>
        <Text fontSize="sm" color="gray.500" mb={2}>
          Add commodity override
        </Text>
        <Select
          options={commodityOptions}
          placeholder="Select commodity"
          onChange={(option) => addCommodityOverride(option?.value)}
          isDisabled={!canEdit || !areCommoditiesReady}
          isClearable={false}
        />
      </Box>

      <Stack spacing={4}>
        {values.commodityOverrides.map((override, index) => (
          <Box
            key={override.commodityId}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius={3}
            overflow="hidden"
          >
            <Row justifyContent="space-between" alignItems="center" px={6} py={1} bg="gray.50">
              <Text fontWeight="bold">{override.commodityName}</Text>
              <Button
                size="sm"
                variant="ghost"
                colorScheme="secondary"
                onClick={() => removeCommodityOverride(override.commodityId)}
                isDisabled={!canEdit}
              >
                Remove
              </Button>
            </Row>
            <ConfigurationPanelFields
              formFields={override.fields}
              fieldPathPrefix={`commodityOverrides.${index}.fields`}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  )
}
